import React, { useEffect, useState } from 'react';
import { Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Typography } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { useTranslation } from 'react-i18next';
import ClientPicker from '../common/ClientPicker';
import SurveyFields from '../jobs/SurveyFields';
import QuoteService from '../../services/QuoteService';
import { errorMessage, fieldErrors } from '../../services/api';
import { useToast } from '../../contexts/ToastContext';

const EMPTY = { client: null, title: '', description: '', municipality: '', cadastralZone: '', parcelNumber: '',
    ownerName: '', coOwners: [], propertySheet: '' };

/** Create or edit a quote's header: client, title, description. Positions (each with its category) are added on the quote page. */
export default function QuoteFormDialog({ open, quote, defaults, onClose, onSaved }) {
    const { t } = useTranslation();
    const { showToast } = useToast();
    const [form, setForm] = useState(EMPTY);
    const [errors, setErrors] = useState({});
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!open) return;
        setErrors({});
        setForm(quote
            ? {
                client: quote.client, title: quote.title, description: quote.description ?? '',
                municipality: quote.municipality ?? '', cadastralZone: quote.cadastralZone ?? '', parcelNumber: quote.parcelNumber ?? '',
                ownerName: quote.ownerName ?? '', coOwners: quote.coOwners || [], propertySheet: quote.propertySheet ?? '',
            }
            : { ...EMPTY, ...(defaults || {}) });
    }, [open, quote, defaults]);

    const set = (k) => (e) => { setForm(f => ({ ...f, [k]: e.target.value })); setErrors(x => ({ ...x, [k]: undefined })); };

    const save = async () => {
        const v = {};
        if (!form.client) v.clientId = t('jobs.errors.clientRequired');
        if (!form.title.trim()) v.title = t('validation.required');
        setErrors(v);
        if (Object.keys(v).length) return;
        const payload = {
            clientId: form.client.id, title: form.title,
            description: form.description,
            municipality: form.municipality, cadastralZone: form.cadastralZone, parcelNumber: form.parcelNumber,
            ownerName: form.ownerName, coOwners: form.coOwners, propertySheet: form.propertySheet,
        };
        setSaving(true);
        try {
            const saved = quote ? await QuoteService.update(quote.id, payload) : await QuoteService.create(payload);
            showToast(quote ? t('quotes.toast.updated') : t('quotes.toast.created', { number: saved.number }), 'success');
            onSaved(saved);
        } catch (err) {
            const fe = fieldErrors(err);
            if (Object.keys(fe).length) setErrors(fe);
            else showToast(errorMessage(err, t('common.error')), 'error');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
            <DialogTitle sx={{ fontWeight: 700, fontSize: 18 }}>{quote ? t('quotes.edit') : t('quotes.new')}</DialogTitle>
            <DialogContent>
                <Grid container spacing={2} sx={{ pt: 1 }}>
                    <Grid size={12}>
                        <ClientPicker value={form.client} onChange={(c) => { setForm(f => ({ ...f, client: c })); setErrors(x => ({ ...x, clientId: undefined })); }}
                                      error={!!errors.clientId} helperText={errors.clientId} />
                    </Grid>
                    <Grid size={12}>
                        <TextField fullWidth size="small" required label={t('quotes.fields.title')} placeholder={t('quotes.fields.titlePlaceholder')}
                                   value={form.title} onChange={set('title')} error={!!errors.title} helperText={errors.title} />
                    </Grid>
                    <Grid size={12}>
                        <TextField fullWidth size="small" multiline minRows={3} label={t('quotes.fields.description')} placeholder={t('quotes.fields.descriptionPlaceholder')}
                                   value={form.description} onChange={set('description')} />
                    </Grid>
                </Grid>
                {/* Optional; copied to the jobs created from the quote. */}
                <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: 0.8, mt: 3, mb: 1 }}>
                    {t('jobs.sections.survey')} · {t('common.optional')}
                </Typography>
                <SurveyFields values={form} client={form.client} onChange={(patch) => setForm(f => ({ ...f, ...patch }))} />
                {!quote && <Typography sx={{ fontSize: 12.5, color: '#94A3B8', mt: 2 }}>{t('quotes.itemsAfterCreate')}</Typography>}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2.5 }}>
                <Button onClick={onClose} disabled={saving} sx={{ color: '#64748B' }}>{t('common.cancel')}</Button>
                <Button variant="contained" onClick={save} disabled={saving} disableElevation sx={{ minWidth: 120 }}>
                    {saving ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : (quote ? t('common.save') : t('quotes.create'))}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
